import { FC } from 'react';
import { useTranslation } from 'next-i18next';

import { Address } from '@/shared/components';
import { renderDtoIdFieldView } from '@/shared/components/forms/fields/renderDtoIdFieldView';
import { SelectFromListFieldView } from '@/shared/components/forms/fields/SelectFromListFieldView';
import {
  Address as AddressType,
  HelperRenderType,
  TDtoWrapper,
  ValueType,
} from '@/shared/lib/forms/form-wrapper.types';
import { SJDatabase } from '@/shared/lib/sj-orm';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/personalIdentifiersDocuments/personalIdentifiersDocuments.dto';
import { Icon } from '@/shared/uikit/Icon';
import { dates } from '@/shared/utils/dates';

import { ExpandableText } from './ExpandableText';

const POTENTIALLY_LONG_TEXT = [
  'comments',
  'storageComments',
  'myTodoComments',
  'other',
  'copyStorageComments',
];

const EMPTY_TEXT = '-';

type PersonalIdentifierFieldValueProps = {
  personalIdentifier: PersonalIdentifierDocumentDto;
  fieldKey: string;
  sjDb: SJDatabase;
  fieldDef: ValueType<
    HelperRenderType,
    TDtoWrapper<Record<string, unknown>, Record<string, HelperRenderType>>,
    unknown
  >;
  isPdf?: boolean;
};

export const PersonalIdentifierFieldValue: FC<PersonalIdentifierFieldValueProps> = ({
  fieldKey,
  personalIdentifier,
  fieldDef,
  sjDb,
  isPdf,
}) => {
  const { t } = useTranslation('common');

  const type = fieldDef?.type;
  const value = personalIdentifier[fieldKey as keyof PersonalIdentifierDocumentDto];

  // Handle attachments/scan fields
  if (['attachments', 'scan'].includes(fieldKey) && value) {
    const fileIds = (Array.isArray(value) ? value : [value]) as string[];
    if (!fileIds.length) return EMPTY_TEXT;

    return fileIds.map((fileId, i) =>
      isPdf ? (
        fileId
      ) : (
        <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
          <Icon name="file-text" size="s16" />
          {fileId}
        </div>
      ),
    );
  }

  switch (type) {
    case QuestionType.PICK_ARRAY_OF_DTO:
      return renderDtoIdFieldView({ ids: value as string[], emptyText: EMPTY_TEXT, sjDb, isPdf });

    case QuestionType.CHECKBOX:
      return value ? t('yes') : t('no');

    case QuestionType.DATE_TIME:
      return value ? dates.formatLongDate(value as string) : EMPTY_TEXT;

    case QuestionType.PHONE_NUMBER: {
      const number = value as { Dropdown: string; Input: string } | undefined;
      return number?.Dropdown && number.Input ? `${number.Dropdown}${number.Input}` : EMPTY_TEXT;
    }

    case QuestionType.SELECT_FROM_LIST: {
      if (!fieldDef) return EMPTY_TEXT;

      const fieldWithItems = fieldDef as unknown as {
        items: { key: string; label: string }[];
        mode?: string;
      };

      return (
        <SelectFromListFieldView
          fieldKey={fieldKey}
          value={value as string | string[]}
          formFieldSelectItems={fieldWithItems.items || []}
          emptyText={EMPTY_TEXT}
          multiple={fieldWithItems.mode === 'multiple'}
          isPdf={isPdf}
        />
      );
    }

    case QuestionType.TEXT_MULTILINE: {
      const textValue = value ? String(value) : EMPTY_TEXT;
      if (textValue === EMPTY_TEXT) return EMPTY_TEXT;

      return isPdf ? textValue : <ExpandableText text={textValue} />;
    }

    case QuestionType.ADDRESS: {
      return <Address value={value as AddressType} isPdf={isPdf} />;
    }

    default: {
      const textValue = value ? String(value) : EMPTY_TEXT;
      // Check if it's a long text field (comments, notes, etc.)
      if (textValue !== EMPTY_TEXT && POTENTIALLY_LONG_TEXT.includes(fieldKey) && !isPdf) {
        return <ExpandableText text={textValue} />;
      }

      return textValue;
    }
  }
};
